// src/pages/Accounting/JournalEntries/JournalEntriesList.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box, IconButton, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import axios from '../../../axios';
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, reportGridSx, fmtMoney, fmtNumber,
} from '../../../components/Reports/ReportKit';
import { getEntryTypes, entryTypeLabel, ENTRY_STATUS, isSystemEntry, apiError } from './journalEntryTypes';

const SYSTEM_HINT = 'قيد آلي — يُعدّل أو يُحذف من العملية الأصلية (فاتورة / سند)';

export default function JournalEntriesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const range = useReportDateRange('today');

  const [entries, setEntries] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [entryType, setEntryType] = useState('');
  const [confirm, setConfirm] = useState(null); // { kind: 'post'|'delete', entry }
  const [busy, setBusy] = useState(false);

  const entryTypes = useMemo(() => getEntryTypes(t), [t]);

  // تأخير البحث أثناء الكتابة
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  // أي تغيير في الفلاتر يرجع للصفحة الأولى
  useEffect(() => {
    setPaginationModel(m => (m.page === 0 ? m : { ...m, page: 0 }));
  }, [debouncedSearch, entryType, range.dateFrom, range.dateTo]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/JournalEntries', {
        params: {
          page: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          search: debouncedSearch || null,
          entryType: entryType || null,
          fromDate: range.dateFrom,
          toDate: range.dateTo,
        },
      });
      setEntries(Array.isArray(data?.items) ? data.items : []);
      setTotalItems(Number(data?.totalItems) || 0);
    } catch (err) {
      console.error('Error fetching entries:', err);
      toast.error(apiError(err, 'تعذّر تحميل القيود'));
    } finally {
      setLoading(false);
    }
  }, [paginationModel, debouncedSearch, entryType, range.dateFrom, range.dateTo]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // ── إجماليات الصفحة المعروضة ──
  const pageTotals = useMemo(() => entries.reduce((a, e) => ({
    debit: a.debit + (Number(e.totalDebit) || 0),
    credit: a.credit + (Number(e.totalCredit) || 0),
    drafts: a.drafts + (e.status === 1 ? 1 : 0),
  }), { debit: 0, credit: 0, drafts: 0 }), [entries]);

  // ── ترحيل / حذف ──
  const runConfirm = async () => {
    if (!confirm) return;
    const { kind, entry } = confirm;
    setBusy(true);
    try {
      if (kind === 'post') {
        await axios.post(`/JournalEntries/${entry.globalId}/post`);
        toast.success(`تم ترحيل القيد رقم ${entry.entryNumber}`);
      } else {
        await axios.delete(`/JournalEntries/${entry.globalId}`);
        toast.success(`تم حذف القيد رقم ${entry.entryNumber}`);
      }
      setConfirm(null);
      fetchEntries();
    } catch (err) {
      toast.error(apiError(err, kind === 'post' ? 'تعذّر ترحيل القيد' : 'تعذّر حذف القيد'));
    } finally {
      setBusy(false);
    }
  };

  const ActionBtn = ({ title, icon, color, onClick, disabled }) => (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small" onClick={onClick} disabled={disabled}
          sx={{
            width: 32, height: 32, borderRadius: '9px', border: '1px solid #e5e7eb', color,
            '&:hover': { bgcolor: `${color}12`, borderColor: color },
            '&.Mui-disabled': { opacity: .35 },
          }}
        >{icon}</IconButton>
      </span>
    </Tooltip>
  );

  const columns = [
    {
      field: 'entryNumber', headerName: 'رقم القيد', width: 110,
      renderCell: p => <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{p.value}</Typography>,
    },
    {
      field: 'date', headerName: 'التاريخ', width: 115,
      valueFormatter: (v) => {
        const val = v?.value !== undefined ? v.value : v; // يدعم v6 و v7
        return val ? dayjs(val).format('DD/MM/YYYY') : '';
      },
    },
    {
      field: 'entryType', headerName: 'النوع', width: 170,
      renderCell: p => {
        const sys = isSystemEntry(p.value);
        return (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: .5, px: 1, py: .35, borderRadius: '999px',
            fontSize: 12.5, fontWeight: 700,
            bgcolor: sys ? '#f1f5f9' : '#eef2ff', color: sys ? '#475569' : '#4338ca',
          }}>
            {sys && <LockOutlinedIcon sx={{ fontSize: 14 }} />}
            {entryTypeLabel(p.value, t)}
          </Box>
        );
      },
    },
    {
      field: 'description', headerName: 'البيان', flex: 1, minWidth: 220,
      renderCell: p => (
        <Tooltip title={p.value || ''} placement="top-start">
          <Typography sx={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'right' }}>
            {p.value || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'totalDebit', headerName: 'مدين', width: 130, type: 'number',
      renderCell: p => <Typography sx={{ fontWeight: 700, fontSize: 14, direction: 'ltr' }}>{fmtMoney(p.value)}</Typography>,
    },
    {
      field: 'totalCredit', headerName: 'دائن', width: 130, type: 'number',
      renderCell: p => <Typography sx={{ fontWeight: 700, fontSize: 14, direction: 'ltr' }}>{fmtMoney(p.value)}</Typography>,
    },
    {
      field: 'status', headerName: 'الحالة', width: 100,
      renderCell: p => {
        const s = ENTRY_STATUS[p.value] || { label: p.value, bg: '#f1f5f9', fg: '#475569' };
        return (
          <Box sx={{ px: 1.1, py: .3, borderRadius: '999px', fontSize: 12.5, fontWeight: 800, bgcolor: s.bg, color: s.fg }}>
            {s.label}
          </Box>
        );
      },
    },
    {
      field: 'actions', headerName: 'إجراءات', width: 180, sortable: false, filterable: false, disableExport: true,
      renderCell: ({ row }) => {
        const sys = isSystemEntry(row.entryType);
        const draft = row.status === 1;
        const lockedTip = sys ? SYSTEM_HINT : 'متاح للمسودات فقط';
        return (
          <Box sx={{ display: 'flex', gap: .6 }}>
            <ActionBtn title="عرض" icon={<VisibilityOutlinedIcon fontSize="small" />} color="#4f46e5"
              onClick={() => navigate(`/journal-entries/${row.globalId}/view`)} />
            <ActionBtn title={draft && !sys ? 'تعديل' : lockedTip} icon={<EditOutlinedIcon fontSize="small" />} color="#0284c7"
              disabled={!draft || sys} onClick={() => navigate(`/journal-entries/${row.globalId}/edit`)} />
            <ActionBtn title={draft && !sys ? 'ترحيل' : lockedTip} icon={<TaskAltIcon fontSize="small" />} color="#059669"
              disabled={!draft || sys} onClick={() => setConfirm({ kind: 'post', entry: row })} />
            <ActionBtn title={draft && !sys ? 'حذف' : lockedTip} icon={<DeleteOutlineIcon fontSize="small" />} color="#e11d48"
              disabled={!draft || sys} onClick={() => setConfirm({ kind: 'delete', entry: row })} />
          </Box>
        );
      },
    },
  ];

  const activeCount = [entryType, debouncedSearch].filter(Boolean).length;

  return (
    <>
      <ReportPage
        title={t('journalEntries', { defaultValue: 'القيود المحاسبية' })}
        icon={<MenuBookOutlinedIcon />}
        period={range}
        actions={[
          { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchEntries, disabled: loading },
          { label: 'قيد افتتاحي', icon: <PlaylistAddOutlinedIcon />, onClick: () => navigate('/journal-entries/new?type=2') },
          { label: 'قيد يومية', icon: <AddIcon />, onClick: () => navigate('/journal-entries/new?type=1'), primary: true },
        ]}
        stats={[
          { label: 'عدد القيود', value: fmtNumber(totalItems), icon: <ReceiptLongOutlinedIcon />, color: 'primary' },
          { label: 'مدين (المعروض)', value: fmtMoney(pageTotals.debit), icon: <TrendingUpIcon />, color: 'info' },
          { label: 'دائن (المعروض)', value: fmtMoney(pageTotals.credit), icon: <TrendingDownIcon />, color: 'success' },
          { label: 'مسودات بانتظار الترحيل', value: fmtNumber(pageTotals.drafts), icon: <EditNoteOutlinedIcon />, color: pageTotals.drafts ? 'warning' : 'slate' },
        ]}
        filters={
          <ReportFilters
            range={range}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="بحث برقم القيد أو البيان أو المرجع..."
            onApply={fetchEntries}
            onReset={() => { range.reset(); setSearch(''); setEntryType(''); }}
            loading={loading}
            activeCount={activeCount}
          >
            <ReportSelect
              label="نوع القيد" value={entryType} onChange={setEntryType}
              options={entryTypes.map(e => ({ value: e.value, label: e.label }))}
            />
          </ReportFilters>
        }
      >
        <ReportTableCard title="قائمة القيود" count={fmtNumber(totalItems)}>
          <DataGrid
            rows={entries}
            columns={columns}
            getRowId={r => r.globalId}
            loading={loading}
            paginationMode="server"
            rowCount={totalItems}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnFilter
            sortingMode="server"
            getRowHeight={() => 52}
            onRowDoubleClick={p => navigate(`/journal-entries/${p.row.globalId}/view`)}
            localeText={{
              noRowsLabel: 'لا توجد قيود في هذه الفترة',
              MuiTablePagination: { labelRowsPerPage: 'عدد الصفوف:', labelDisplayedRows: ({ from, to, count }) => `${from}–${to} من ${count}` },
            }}
            sx={reportGridSx}
          />
        </ReportTableCard>
      </ReportPage>

      {/* تأكيد الترحيل / الحذف */}
      <Dialog
        open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth dir="rtl"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {confirm?.kind === 'post' ? 'ترحيل القيد' : 'حذف القيد'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirm?.kind === 'post' ? 'هل تريد ترحيل' : 'هل تريد حذف'} القيد رقم <strong>{confirm?.entry.entryNumber}</strong> بمبلغ{' '}
            <strong dir="ltr">{fmtMoney(confirm?.entry.totalDebit)}</strong>؟
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 13, color: confirm?.kind === 'post' ? '#b45309' : '#be123c' }}>
            {confirm?.kind === 'post'
              ? 'بعد الترحيل يؤثر القيد على أرصدة الحسابات ولا يمكن تعديله.'
              : 'سيُحذف القيد نهائيًا ولا يمكن التراجع.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ fontWeight: 700 }}>إلغاء</Button>
          <Button
            onClick={runConfirm} variant="contained" disableElevation disabled={busy}
            color={confirm?.kind === 'post' ? 'success' : 'error'}
            sx={{ fontWeight: 700, borderRadius: '10px' }}
          >
            {busy ? 'جاري التنفيذ...' : confirm?.kind === 'post' ? 'ترحيل' : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
